import React, { useState, useEffect } from "react";
import Modal from "@/components/modal";
import API from "@/services/api";
import toast from "react-hot-toast";
import { FiUser } from "react-icons/fi";

function UserCard({ user }) {
  return (
    <div className="p-3 hover:bg-gray-50 transition-colors duration-150">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-900">{user.firstname || user.lastname ? `${user.firstname || ""} ${user.lastname || ""}`.trim() : "-"}</div>
          <div className="text-xs text-gray-600 mt-1">{user.email || "-"}</div>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => window.open(`/auth/connect?user_id=${user._id}&token=${API.getToken()}`, "_blank")}
          className="flex-1 h-8 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
        >
          <FiUser size={12} />
          Login as
        </button>
      </div>
    </div>
  );
}

export default function OrganizationModal({ isOpen, onClose, organization, fetchData }) {
  const [activeTab, setActiveTab] = useState("view");
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (organization) {
      setFormData(organization);
      fetchUsers();
    }
  }, [organization]);

  const fetchUsers = async () => {
    if (!organization) return;
    const orgId = organization._id || organization.id;
    if (!orgId) return;

    try {
      setLoadingUsers(true);
      const response = await API.post("/user/search", {
        organization_id: orgId,
        role_recruiter: true,
        limit: 100,
      });

      if (response.ok) {
        setUsers(response.data || []);
      } else {
        toast.error(response.code || "Error fetching users");
      }
    } catch (e) {
      console.error("Error fetching users:", e);
      toast.error(e.code || e.message || "Error fetching users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail || !inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    if (!organization) return;

    try {
      setInviting(true);
      const response = await API.post("/user/invite", {
        email: inviteEmail.trim(),
        organization_id: organization._id,
        organization_name: organization.name,
        role_recruiter: true,
      });

      if (response.ok) {
        toast.success("User invited successfully");
        setInviteEmail("");
        fetchUsers();
      } else {
        toast.error(response.code || "Failed to invite user");
      }
    } catch (e) {
      console.error("Error inviting user:", e);
      toast.error(e.code || "Failed to invite user");
    } finally {
      setInviting(false);
    }
  };

  const handleFormChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!organization) return;
    const orgId = organization._id || organization.id;
    if (!orgId) return;

    try {
      setLoading(true);
      const response = await API.put(`/organization/${orgId}`, formData);

      if (response.ok) {
        toast.success("Organization updated");
        if (fetchData) fetchData();
        onClose();
      } else {
        toast.error(response.code || "Error updating organization");
      }
    } catch (e) {
      console.error("Error updating organization:", e);
      toast.error(e.code || e.message || "Error updating organization");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this organization?")) return;

    try {
      setLoading(true);
      const response = await API.remove(`/organization/${organization._id}`);
      if (response.ok) {
        toast.success("Organization deleted");
        onClose();
        fetchData();
      }
    } catch (e) {
      console.error("Error deleting organization:", e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  if (!organization) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-7xl w-full">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Organization</h2>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab("view")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "view" ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Information
            </button>
            <button
              onClick={() => setActiveTab("raw")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "raw" ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Raw
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "raw" ? (
          <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-sm max-h-[70vh]">{JSON.stringify(organization, null, 2)}</pre>
        ) : (
          <div className="grid grid-cols-12 gap-6">
            {/* Users List - Left Side */}
            <div className="col-span-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Users ({users.length})</h3>

              {/* Invite User Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-2">Invite User</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Email address"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !inviting) {
                        handleInviteUser();
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                    disabled={inviting}
                  />
                  <button
                    onClick={handleInviteUser}
                    disabled={inviting || !inviteEmail.trim()}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {inviting ? "Inviting..." : "Invite"}
                  </button>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg max-h-[70vh] overflow-auto">
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500 text-sm">No users found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {users.map((user) => {
                      const userId = user._id || user.id;
                      return <UserCard key={userId} user={user} fetchUsers={fetchUsers} />;
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Organization Details - Right Side */}
            <div className="col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={formData.name ?? ""}
                    onChange={(e) => handleFormChange("name", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Approved</label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.approved || false}
                      onChange={(e) => handleFormChange("approved", e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">Approved</span>
                  </label>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Career Website Slug</label>
                  <input
                    type="text"
                    value={formData.career_website_slug ?? ""}
                    onChange={(e) => handleFormChange("career_website_slug", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Company ID</label>
                  <input
                    type="text"
                    value={formData.linkedin_company_id ?? ""}
                    onChange={(e) => handleFormChange("linkedin_company_id", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                  <input
                    type="text"
                    value={formData.created_at ? new Date(formData.created_at).toLocaleString("fr-FR") : "-"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                    readOnly
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                  <input
                    type="text"
                    value={formData.updated_at ? new Date(formData.updated_at).toLocaleString("fr-FR") : "-"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                    readOnly
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={handleDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors">
                  {loading ? "Deleting..." : "Delete"}
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
